import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { TrendingUp, AlertCircle } from 'lucide-react';
import BackfillDayModal from './BackfillDayModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAY_LABELS = { mon: 'M', tue: 'T', wed: 'W', thu: 'T', fri: 'F', sat: 'S', sun: 'S' };
const WEEKDAY_FULL = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };

// Tint a bar based on completion ratio (0 → grey, 1 → emerald, midway → amber).
function ratioColor(ratio) {
  if (ratio === null || ratio === undefined) return 'bg-muted';
  if (ratio >= 0.8) return 'bg-emerald-500';
  if (ratio >= 0.5) return 'bg-amber-400';
  if (ratio > 0) return 'bg-orange-400';
  return 'bg-slate-200';
}

export default function SlotCompletionSparkline({ getAuthHeader }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backfillDate, setBackfillDate] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/checkin/completion?days=14`, { headers: getAuthHeader() });
        if (!cancelled) setData(r.data);
      } catch (e) {
        // Silent — widget hides itself on error
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeader, refreshTrigger]);

  // Determine which bars are tappable (within 30-day backdate window)
  const todayDate = new Date();
  const isDateEditable = (dateStr) => {
    const d = new Date(dateStr);
    const daysAgo = Math.floor((todayDate - d) / (1000 * 60 * 60 * 24));
    return daysAgo >= 0 && daysAgo <= 30;
  };

  if (loading) return null;
  if (!data || !data.days || data.days.length === 0) return null;

  const days = data.days;
  // If literally nothing scheduled across the full window, hide the widget.
  const totalScheduled = days.reduce((s, d) => s + (d.scheduled || 0), 0);
  if (totalScheduled === 0) return null;

  const totalLogged = days.reduce((s, d) => s + (d.logged || 0), 0);
  const overallPct = Math.round((totalLogged / totalScheduled) * 100);

  // Per-day height — minimum 8% so empty days are still visible.
  const heightFor = (ratio) => {
    if (ratio === null || ratio === undefined) return '8%';
    const pct = Math.max(8, Math.round(ratio * 100));
    return `${pct}%`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-border p-5"
      data-testid="slot-completion-sparkline"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-[#1A1A1A]">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            Slot completion (14 days)
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            How often you took what you scheduled · <span className="italic">tap any bar to backfill</span>
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-emerald-700 leading-none" data-testid="completion-overall-pct">{overallPct}%</p>
          <p className="text-[10px] text-muted-foreground mt-1">{totalLogged}/{totalScheduled}</p>
        </div>
      </div>

      {/* Sparkline bars */}
      <div className="h-20 flex items-end gap-1.5">
        {days.map((d, i) => {
          const wd = WEEKDAY_LABELS[d.weekday] || '?';
          const full = WEEKDAY_FULL[d.weekday] || d.weekday;
          const pct = d.ratio !== null && d.ratio !== undefined ? Math.round(d.ratio * 100) : null;
          const editable = isDateEditable(d.date);
          const tip = d.scheduled === 0
            ? `${full} ${d.date} — no scheduled supplements${editable ? ' · tap to backfill' : ''}`
            : `${full} ${d.date} — ${d.logged}/${d.scheduled} (${pct}%)${editable ? ' · tap to edit' : ''}`;
          return (
            <button
              key={d.date}
              type="button"
              disabled={!editable}
              onClick={() => editable && setBackfillDate(d.date)}
              className={`flex-1 flex flex-col items-center justify-end h-full gap-1 group ${
                editable ? 'cursor-pointer' : 'cursor-default'
              }`}
              title={tip}
              data-testid={`completion-bar-${i}`}
            >
              <div className="w-full flex-1 flex items-end">
                <div
                  className={`w-full rounded-t transition-all ${ratioColor(d.ratio)} ${
                    editable
                      ? 'group-hover:opacity-80 group-hover:scale-y-105 group-active:scale-y-95'
                      : ''
                  }`}
                  style={{ height: heightFor(d.ratio) }}
                />
              </div>
              <span className="text-[10px] text-muted-foreground leading-none">{wd}</span>
            </button>
          );
        })}
      </div>

      {/* Worst-weekday callout */}
      {data.worst_weekday && data.weekday_ratios?.[data.worst_weekday] !== null && data.weekday_ratios[data.worst_weekday] < 0.5 && (
        <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid="worst-weekday-callout">
          <AlertCircle className="w-3.5 h-3.5 text-amber-700 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-800 leading-snug">
            You miss <span className="font-semibold">{WEEKDAY_FULL[data.worst_weekday]}s</span> the most ({Math.round(data.weekday_ratios[data.worst_weekday] * 100)}% logged). A small habit anchor that day could help.
          </p>
        </div>
      )}

      {/* Legend */}
      <div className="mt-2 flex items-center justify-end gap-3 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500"></span>≥80%</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-400"></span>50–79%</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-orange-400"></span>1–49%</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-200"></span>0%</span>
      </div>

      <BackfillDayModal
        open={!!backfillDate}
        date={backfillDate}
        onClose={() => setBackfillDate(null)}
        onSaved={() => setRefreshTrigger((t) => t + 1)}
        getAuthHeader={getAuthHeader}
      />
    </motion.div>
  );
}
